// -----------------------------------------------------------------------
// JCAMP-DX parsing override for jsv2.DataParser
// -----------------------------------------------------------------------
// jsv2's built-in parseJcampFile only recognises the exact header
// "##XYDATA= (X++(Y..Y))" (with a space), splits values on single spaces,
// and ignores XFACTOR/YFACTOR/DELTAX/units. Standard files such as NIST IR
// spectra ("##XYDATA=(X++(Y..Y))", YUNITS=TRANSMITTANCE) therefore produce
// an empty or NaN-filled spectrum. This replaces it with a parser for the
// uncompressed (AFFN) JCAMP-DX forms, applied to every DataParser instance
// (primary and mirror).
//
// IR transmittance data is converted to normalized absorption-like intensity
// (1 - T/Tmax) so it matches the IRIS XML data: baseline at 0, peaks up in
// data space, which the IR viewer's reversed y-axis draws pointing down.

import * as jsv2 from 'jsv2';

// JCAMP labels compare case-insensitively, ignoring spaces, dashes, slashes and underscores
function normalizeLabel(label) {
    return label.toUpperCase().replace(/[\s\-\/_]/g, "");
}

// Split a JCAMP file into its labelled data records: { LABEL: { value, lines } }
function readRecords(content) {
    const records = {};
    let current = null;
    for (const rawLine of content.replace(/\r\n?/g, "\n").split("\n")) {
        const line = rawLine.replace(/\$\$.*$/, "");  // strip $$ comments
        const match = line.match(/^##([^=]*)=(.*)$/);
        if (match) {
            const label = normalizeLabel(match[1]);
            current = { value: match[2].trim(), lines: [] };
            // keep the first occurrence (multi-block files repeat labels)
            if (!(label in records)) records[label] = current;
        } else if (current && line.trim()) {
            current.lines.push(line.trim());
        }
    }
    return records;
}

function numberOrNull(records, label) {
    const value = records[label] ? parseFloat(records[label].value) : NaN;
    return isNaN(value) ? null : value;
}

// AFFN numbers may be separated by spaces, commas, or just a sign ("1.0-0.5")
const AFFN_NUMBER = /[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;

function parseNumbers(line) {
    // ASDF compression (SQZ/DIF/DUP) uses letters and @%; we only support plain numbers
    if (/[@%A-Za-z]/.test(line.replace(/([\d.])[eE][+-]?\d/g, "$1"))) {
        throw new Error("Compressed JCAMP-DX (ASDF) data is not supported; export the spectrum as uncompressed (AFFN) JCAMP-DX or XY.");
    }
    return (line.match(AFFN_NUMBER) || []).map(parseFloat);
}

// ##XYDATA=(X++(Y..Y)): each line is an X value followed by consecutive Y values
function parseXPlusPlusY(lines, records) {
    const xFactor = numberOrNull(records, "XFACTOR") || 1;
    const yFactor = numberOrNull(records, "YFACTOR") || 1;
    let deltaX = numberOrNull(records, "DELTAX");
    if (deltaX === null) {
        const firstX = numberOrNull(records, "FIRSTX");
        const lastX = numberOrNull(records, "LASTX");
        const nPoints = numberOrNull(records, "NPOINTS");
        if (firstX !== null && lastX !== null && nPoints > 1) {
            deltaX = (lastX - firstX) / (nPoints - 1);
        }
    }

    const rows = lines.map(parseNumbers).filter((nums) => nums.length >= 2);  // skip X-only lines
    const x = [], y = [];
    rows.forEach((nums, i) => {
        const xStart = nums[0] * xFactor;
        const ys = nums.slice(1);
        // Without DELTAX, infer the spacing from the next line's X (or the previous spacing)
        let step = deltaX;
        if (step === null) {
            step = rows[i + 1] ? (rows[i + 1][0] * xFactor - xStart) / ys.length : (x.length > 1 ? x[x.length - 1] - x[x.length - 2] : 0);
        }
        ys.forEach((yValue, j) => {
            x.push(xStart + j * step);
            y.push(yValue * yFactor);
        });
    });
    return { x, y };
}

// ##XYPOINTS= / ##PEAKTABLE=(XY..XY): X,Y pairs
function parseXYPairs(lines, records) {
    const xFactor = numberOrNull(records, "XFACTOR") || 1;
    const yFactor = numberOrNull(records, "YFACTOR") || 1;
    const nums = lines.flatMap(parseNumbers);
    const x = [], y = [];
    for (let i = 0; i + 1 < nums.length; i += 2) {
        x.push(nums[i] * xFactor);
        y.push(nums[i + 1] * yFactor);
    }
    return { x, y };
}

export function parseJcamp(content, dataType) {
    const records = readRecords(content);

    let xy;
    const xyData = records["XYDATA"];
    const pairs = records["XYPOINTS"] || records["PEAKTABLE"];
    if (xyData && /X\+\+\s*\(\s*Y\.\.Y\s*\)/i.test(xyData.value)) {
        xy = parseXPlusPlusY(xyData.lines, records);
    } else if (pairs) {
        xy = parseXYPairs(pairs.lines, records);
    } else {
        throw new Error("No ##XYDATA=(X++(Y..Y)), ##XYPOINTS or ##PEAKTABLE data found in JCAMP-DX file.");
    }

    // Drop NaN points; one NaN breaks the viewer's min/max boundary calculation
    let x = [], y = [];
    xy.x.forEach((xValue, i) => {
        if (isFinite(xValue) && isFinite(xy.y[i])) {
            x.push(xValue);
            y.push(xy.y[i]);
        }
    });
    if (x.length === 0) throw new Error("JCAMP-DX file contains no numeric spectrum data.");

    const xUnits = records["XUNITS"] ? records["XUNITS"].value.toUpperCase() : "";
    const yUnits = records["YUNITS"] ? records["YUNITS"].value.toUpperCase() : "";

    if (dataType === "1D-IR") {
        // Wavelength in micrometres -> wavenumber in cm^-1
        if (xUnits.startsWith("MICROMETER")) x = x.map((v) => 1e4 / v);

        const yMax = Math.max(...y);
        if (yUnits.includes("TRANSMITTANCE") && yMax > 0) {
            // T (or %T) -> normalized absorption-like intensity, baseline 0
            y = y.map((v) => 1 - v / yMax);
        } else if (yMax > 0) {
            y = y.map((v) => v / yMax);
        }
    }

    return {
        x,
        y,
        dataType,
        title: records["TITLE"] ? records["TITLE"].value : null,
    };
}

jsv2.DataParser.prototype.parseJcampFile = function(content) {
    console.log("Parsing JCAMP file (app parser)");
    return parseJcamp(content, this.data_type);
};
